import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAuthCapabilities,
  loginModerator,
  logoutModerator,
  type AuthCapabilities,
} from '../../lib/moderationApi';
import { ModerationDetailPage } from './ModerationDetailPage';
import { ModerationLogin } from './ModerationLogin';
import { ModerationQueue } from './ModerationQueue';
import { ErrorMessage, secondaryButtonClass } from './ModerationUi';

export type ModerationRoute =
  | { kind: 'login' }
  | { kind: 'queue' }
  | { kind: 'detail'; publicId: string }
  | { kind: 'invalid' };

const anonymousCapabilities: AuthCapabilities = {
  isAuthenticated: false,
  isReviewer: false,
  isSuperuser: false,
  username: null,
};

export function ModerationPage({
  route,
  locationSearch,
  onNavigate,
}: {
  route: ModerationRoute;
  locationSearch: string;
  onNavigate: (path: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [logoutError, setLogoutError] = useState<unknown>(null);
  const auth = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: getAuthCapabilities,
    staleTime: 30_000,
  });
  const capabilities = auth.data ?? anonymousCapabilities;
  const hasModerationAccess = capabilities.isReviewer || capabilities.isSuperuser;

  useEffect(() => {
    if (route.kind === 'login' && capabilities.isAuthenticated && hasModerationAccess) {
      onNavigate('/moderation/manual-corrections');
    }
  }, [capabilities.isAuthenticated, hasModerationAccess, onNavigate, route.kind]);

  const login = async (username: string, password: string) => {
    const result = await loginModerator(username, password);
    queryClient.setQueryData(['auth', 'me'], result);
    if (result.isReviewer || result.isSuperuser) {
      onNavigate('/moderation/manual-corrections');
    }
  };

  const logout = async () => {
    setLogoutError(null);
    try {
      await logoutModerator();
      queryClient.clear();
      queryClient.setQueryData(['auth', 'me'], anonymousCapabilities);
      onNavigate('/moderation/login');
    } catch (error) {
      setLogoutError(error);
    }
  };

  const refreshAuthorization = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [queryClient]);

  if (auth.isLoading) {
    return <main role="status" className="mx-auto max-w-5xl p-10 text-center">Проверка сессии…</main>;
  }
  if (auth.error) {
    return <main className="mx-auto max-w-3xl p-6"><ErrorMessage error={auth.error} /></main>;
  }
  if (!capabilities.isAuthenticated) {
    return <ModerationLogin onLogin={login} />;
  }
  if (!hasModerationAccess) {
    return (
      <main className="mx-auto max-w-3xl space-y-4 p-6">
        <h1 className="text-2xl font-semibold">Доступ запрещён</h1>
        <p>Активная сессия не имеет прав reviewer или superuser.</p>
        <button className={secondaryButtonClass} type="button" onClick={logout}>Выйти</button>
      </main>
    );
  }

  return (
    <>
      <div className="mx-auto flex w-full max-w-7xl items-center justify-end gap-3 px-4 pt-4 sm:px-6">
        <span className="text-sm text-muted-foreground">
          Аккаунт: {capabilities.username}
        </span>
        <button className={secondaryButtonClass} type="button" onClick={logout}>Выйти</button>
      </div>
      {logoutError ? (
        <div className="mx-auto mt-3 max-w-7xl px-4 sm:px-6"><ErrorMessage error={logoutError} /></div>
      ) : null}
      {route.kind === 'queue' ? (
        <ModerationQueue
          locationSearch={locationSearch}
          onNavigate={onNavigate}
          onAuthorizationFailure={refreshAuthorization}
        />
      ) : route.kind === 'detail' ? (
        <ModerationDetailPage
          publicId={route.publicId}
          onNavigate={onNavigate}
          onAuthorizationFailure={refreshAuthorization}
        />
      ) : route.kind === 'invalid' ? (
        <main className="mx-auto max-w-3xl space-y-4 p-6">
          <h1 className="text-2xl font-semibold">Страница модерации не найдена</h1>
          <button className={secondaryButtonClass} onClick={() => onNavigate('/moderation/manual-corrections')}>
            Перейти в очередь
          </button>
        </main>
      ) : (
        <main role="status" className="mx-auto max-w-3xl p-6">Переход в очередь…</main>
      )}
    </>
  );
}
